import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable, HpiDateFilterBar, HpiPagination } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------- */

interface TimeEntry {
  id: number
  date: string | null
  service_type: string | null
  user_name: string | null
  contact_name: string | null
  notes: string | null
  started_at: string | null
  ended_at: string | null
  otj_minutes: number
  admin_minutes: number
  travel_minutes: number
  total_minutes: number
  flat_cost: number | null
  mileage: number
  created_at: string | null
  submitted_at: string | null
  approved_at: string | null
  marketing_log_id: number | null
  marketing_customer_id: number | null
}

interface Summary {
  total_minutes: number
  total_mileage: number
  entry_count: number
}

interface Props {
  customer: { id: number; display_name: string }
  contact: { id: number; display_name: string } | null
  time_entries: TimeEntry[]
  summary: Summary
  start_date: string | null
  end_date: string | null
}

/* -- Helpers ----------------------------------------------- */

function formatTimeHM(minutes: number): string {
  if (minutes <= 0) return ""
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  return `${h}h ${m.toString().padStart(2, "0")}m`
}

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined || amount === 0) return ""
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* -- Columns ----------------------------------------------- */

const PER_PAGE = 50

function buildColumns(
  hasMileage: boolean,
  showStats: boolean,
): HpiColumn<TimeEntry>[] {
  const cols: HpiColumn<TimeEntry>[] = [
    {
      header: "Date",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-sm">
          {row.marketing_log_id ? (
            <Link
              href={`/customers/${row.marketing_customer_id}/logs/${row.marketing_log_id}`}
              className="text-blue-600 hover:underline"
            >
              {formatDate(row.date)}
            </Link>
          ) : (
            formatDate(row.date)
          )}
        </div>
      ),
    },
    {
      header: "Type",
      width: "max-w-[120px]",
      render: (row) => <div className="text-sm">{row.service_type ?? ""}</div>,
    },
    {
      header: "Description",
      render: (row) => (
        <div className="text-sm">
          <div className="font-medium">{row.user_name}</div>
          {row.contact_name && (
            <div className="text-muted-foreground text-xs">
              {row.contact_name}
            </div>
          )}
          {row.otj_minutes > 0 && (
            <div className="text-muted-foreground text-xs">
              {row.started_at} to {row.ended_at}
              {row.travel_minutes > 0 &&
                ` | Travel: ${formatTimeHM(row.travel_minutes)}`}
            </div>
          )}
          {row.otj_minutes === 0 &&
            (row.admin_minutes > 0 || row.travel_minutes > 0) && (
              <div className="text-muted-foreground text-xs">
                {row.admin_minutes > 0 &&
                  `Admin: ${formatTimeHM(row.admin_minutes)}`}
                {row.admin_minutes > 0 && row.travel_minutes > 0 && " | "}
                {row.travel_minutes > 0 &&
                  `Travel: ${formatTimeHM(row.travel_minutes)}`}
              </div>
            )}
          {row.notes && (
            <div className="text-muted-foreground text-xs italic">
              {row.notes}
            </div>
          )}
        </div>
      ),
    },
    {
      header: "Total",
      width: "max-w-[100px]",
      align: "right",
      render: (row) => (
        <div className="text-sm">
          <div>{formatTimeHM(row.total_minutes)}</div>
          {row.flat_cost != null && row.flat_cost > 0 && (
            <div className="text-muted-foreground text-xs">
              {formatCurrency(row.flat_cost)}
            </div>
          )}
        </div>
      ),
    },
  ]

  if (hasMileage) {
    cols.push({
      header: "Miles",
      width: "max-w-[80px]",
      align: "right",
      hideOnMobile: true,
      render: (row) => (
        <div className="text-sm">{row.mileage > 0 ? row.mileage : ""}</div>
      ),
    })
  }

  if (showStats) {
    cols.push(
      {
        header: "Added",
        width: "max-w-[100px]",
        hideOnMobile: true,
        render: (row) => (
          <div className="text-sm">{formatDate(row.created_at)}</div>
        ),
      },
      {
        header: "Submitted",
        width: "max-w-[100px]",
        hideOnMobile: true,
        render: (row) => (
          <div className="text-sm">{formatDate(row.submitted_at)}</div>
        ),
      },
      {
        header: "Approved",
        width: "max-w-[100px]",
        hideOnMobile: true,
        render: (row) => (
          <div className="text-sm">{formatDate(row.approved_at)}</div>
        ),
      },
    )
  }

  return cols
}

/* -- Main Component ---------------------------------------- */

export default function CustomerTimeReport({
  customer,
  contact,
  time_entries,
  summary,
  start_date,
  end_date,
}: Props) {
  const [startDate, setStartDate] = useState(start_date ?? "")
  const [endDate, setEndDate] = useState(end_date ?? "")
  const [page, setPage] = useState(1)
  const [showStats, setShowStats] = useState(false)

  const totalPages = Math.ceil(time_entries.length / PER_PAGE)
  const paginated = time_entries.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const hasMileage = time_entries.some((e) => e.mileage > 0)
  const columns = buildColumns(hasMileage, showStats)

  const title = contact
    ? `Time Report - ${contact.display_name}`
    : `Time Report - ${customer.display_name}`

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Time Report", href: "#" },
  ]

  const handleUpdate = () => {
    setPage(1)
    router.get(
      `/customers/${customer.id}/reports/time`,
      {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        contact_id: contact?.id ?? undefined,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={title} />

      <div className="p-6">
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showStats}
              onChange={(e) => setShowStats(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show status dates
          </label>
        </HpiDateFilterBar>

        <HpiDataTable<TimeEntry>
          title={title}
          columns={columns}
          data={paginated}
          keyFn={(row) => row.id}
          emptyMessage="No time entries found for this customer."
          pagination={
            time_entries.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={time_entries.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {time_entries.length > 0 && (
          <div className="mt-2 flex justify-end gap-8 text-sm font-semibold">
            <span>{summary.entry_count} Entries</span>
            <span>Total: {formatTimeHM(summary.total_minutes)}</span>
            {summary.total_mileage > 0 && (
              <span>{summary.total_mileage} Miles</span>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
